import { RequestOptions, Response } from './BaseRemoteApi'

const AuthenticationErrorType = 'AuthenticationError';
const ForbiddenErrorType = 'ForbiddenError';
const NotFoundErrorType = 'NotFound';
const ServerErrorType = 'ServerError';
const ConnectionLostErrorType = 'ConnectionLostError';
const UnknownErrorType = 'UnknownError';

export type RemoteApiErrorType =
  'AuthenticationError' |
  'ForbiddenError' |
  'ProjectNotFound' |
  'NotFound' |
  'ServerError' |
  'ConnectionLostError' |
  'UnknownError'

type ErrorBodyDetail = {
  message: string;
  code: string;
  arguments: string[];
}

type ErrorBody = {
  name?: string;
  status?: string;
  message?: string;
  details?: ErrorBodyDetail[]
}

export type RemoteApiError = {
  type: RemoteApiErrorType;
  message: string;
  errorCode: number;
  errorBody?: ErrorBody | string;
  response: Response;
  options: RequestOptions;
}

export class RemoteApiFetchErrorFactory {

  static buildError(response: any, options: RequestOptions, content: any): RemoteApiError {
    switch (response.status) {
      case 401:
        return RemoteApiFetchErrorFactory.authenticationError(response, options, content);
      case 403:
        return RemoteApiFetchErrorFactory.forbiddenError(response, options, content);
      case 404:
        return RemoteApiFetchErrorFactory.notFoundError(response, options, content);
      case 500:
        return RemoteApiFetchErrorFactory.serverError(response, options, content);
      default:
        return RemoteApiFetchErrorFactory.unknownError(response, options, content)
    }
  }

  static fromResponseAndOptions(response: any, options: RequestOptions): Promise<RemoteApiError> {
    return response.text().then((content: string) => {
      try {
        const json = JSON.parse(content);
        return RemoteApiFetchErrorFactory.buildError(response, options, json)
      } catch (err) {
        return RemoteApiFetchErrorFactory.buildError(response, options, content)
      }
    })
  }

  static authenticationError = (response: Response, options: RequestOptions, content: any): RemoteApiError => {
    return {
      type: AuthenticationErrorType,
      message: 'Not authenticated',
      errorCode: 401,
      errorBody: content,
      response: response,
      options: options
    }
  };

  static connectionLostError = (err: any, options: RequestOptions, content: any): RemoteApiError => {
    return {
      type: ConnectionLostErrorType,
      message: 'Connection lost ',
      errorCode: 0,
      errorBody: content,
      response: err,
      options: options
    }
  };

  static forbiddenError = (response: Response, options: RequestOptions, body: any): RemoteApiError => {
    return {
      type: ForbiddenErrorType,
      message: 'Not allowed',
      errorCode: 403,
      errorBody: body,
      response: response,
      options: options
    }
  };

  static notFoundError = (response: Response, options: RequestOptions, body: any): RemoteApiError => {
    return {
      type: NotFoundErrorType,
      message: 'Not found',
      errorCode: 404,
      errorBody: body,
      response: response,
      options: options
    }
  };

  static serverError = (response: Response, options: RequestOptions, body: any): RemoteApiError => {
    return {
      type: ServerErrorType,
      message: body && body.message ? body.message : 'Server error',
      errorCode: 500,
      errorBody: body,
      response: response,
      options: options
    }
  };

  static unknownError = (response: Response, options: RequestOptions, body: any): RemoteApiError => {
    return {
      type: UnknownErrorType,
      message: body && body.message ? body.message : 'Unexpected error ' + response.status,
      errorCode: response.status,
      errorBody: body,
      response: response,
      options: options
    }
  }

  static apiErrorWhiteList = (): string[] => {
    return [
      'apimanager',
      'armui'
    ]
  }
}
